/**
 * Predictive search panel. Fetches result markup from the `predictive-search`
 * section (theme/sections/predictive-search.liquid) via the Predictive
 * Search Suggestions API and swaps it into the results container as the
 * customer types.
 *
 * The toggle button lives in the header - outside this element - so, like
 * `<flyout-cart>`, the open listener is bound on `document` rather than
 * assumed to be inside this element's own subtree. connectedCallback bails
 * out entirely if the input/results markup isn't present, so this file is
 * a no-op if the predictive search Liquid is removed for a lower tier.
 */
if (!customElements.get('predictive-search')) {
  customElements.define(
    'predictive-search',
    class PredictiveSearch extends HTMLElement {
      connectedCallback() {
        this.input = this.querySelector('[data-predictive-search-input]');
        this.results = this.querySelector('[data-predictive-search-results]');
        if (!this.input || !this.results) return;

        this.clearButton = this.querySelector('[data-predictive-search-clear]');
        this.submitButton = this.querySelector('[data-predictive-search-submit]');
        this.header = document.querySelector('.header');
        this.debounceTimer = null;
        this.abortController = null;
        this.lastFocused = null;

        this.updateOffset = this.updateOffset.bind(this);
        this.updateOffset();
        window.addEventListener('resize', this.updateOffset);

        this.input.addEventListener('input', () => this.onInput());

        this.input.addEventListener('keydown', (event) => {
          if (event.key !== 'Enter') return;
          event.preventDefault();
          this.submit();
        });

        this.addEventListener('click', (event) => {
          if (event.target.closest('[data-predictive-search-close]')) this.close();
          if (event.target.closest('[data-predictive-search-clear]')) this.clear();
          if (event.target.closest('[data-predictive-search-submit]')) this.submit();
        });

        document.addEventListener('click', (event) => {
          const toggle = event.target.closest('[data-predictive-search-toggle]');
          if (!toggle) return;
          event.preventDefault();
          this.open();
        });

        document.addEventListener('keydown', (event) => {
          if (event.key === 'Escape' && !this.hidden) this.close();
        });
      }

      updateOffset() {
        const offset = this.header ? Math.max(0, this.header.getBoundingClientRect().bottom) : 0;
        this.style.setProperty('--predictive-search-offset', `${offset}px`);
      }

      open() {
        document.dispatchEvent(new CustomEvent('mobilemenu:close'));

        this.updateOffset();
        this.lastFocused = document.activeElement;
        this.hidden = false;
        document.body.style.overflow = 'hidden';
        this.input.focus();

        document.querySelectorAll('[data-predictive-search-toggle]').forEach((toggle) => {
          toggle.setAttribute('aria-expanded', 'true');
        });
      }

      close() {
        this.hidden = true;
        document.body.style.overflow = '';

        document.querySelectorAll('[data-predictive-search-toggle]').forEach((toggle) => {
          toggle.setAttribute('aria-expanded', 'false');
        });

        if (this.lastFocused) this.lastFocused.focus();
      }

      clear() {
        this.input.value = '';
        if (this.clearButton) this.clearButton.hidden = true;
        this.input.focus();
        this.renderResults('');
      }

      onInput() {
        const term = this.input.value.trim();
        if (this.clearButton) this.clearButton.hidden = term === '';

        clearTimeout(this.debounceTimer);
        this.debounceTimer = setTimeout(() => this.search(term), 300);
      }

      submit() {
        const term = this.input.value.trim();
        if (term === '') return;

        clearTimeout(this.debounceTimer);
        window.location.href = `${this.dataset.searchUrl}?q=${encodeURIComponent(term)}`;
      }

      async search(term) {
        if (term === '') {
          if (this.abortController) this.abortController.abort();
          this.renderResults('');
          return;
        }

        if (this.abortController) this.abortController.abort();
        this.abortController = new AbortController();

        this.classList.add('loading');

        try {
          // Built by hand rather than with URLSearchParams: Shopify's
          // Predictive Search Suggestions API expects literal `[` `]` in
          // `resources[type]` / `resources[options][...]` - URLSearchParams
          // percent-encodes them (`%5B`/`%5D`), which the endpoint doesn't
          // recognize, so it silently ignores the resource-type filter and
          // returns no product/collection/page/article results at all.
          const url =
            `${this.dataset.predictiveSearchUrl}?q=${encodeURIComponent(term)}` +
            '&resources[type]=product,collection,page,article' +
            '&resources[options][unavailable_products]=last' +
            '&section_id=predictive-search';
          const response = await fetch(url, { signal: this.abortController.signal });
          const html = await response.text();
          this.renderResults(html);
        } catch (error) {
          if (error.name !== 'AbortError') this.renderResults('');
        } finally {
          this.classList.remove('loading');
        }
      }

      renderResults(html) {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const newResults = doc.querySelector('[data-predictive-search-results]');
        this.results.innerHTML = newResults ? newResults.innerHTML : '';
      }
    }
  );
}
